import type React from 'react';
import {useState} from 'react';
import {useParams} from 'react-router';
import {Autocomplete, Button, Dialog, DialogActions, DialogContent, DialogTitle, MenuItem, Stack, TextField} from '@mui/material';
import {type AppUser, useAppUsersQuery} from '../../hooks/useCategoryRecord';
import type {TeamMemberInput} from '../../types/categoryRecordTypes';

type Props = {
    title: string;
    submitLabel: string;
    roles: string[];
    raci: string[];
    /** RACI the dialog starts on (the category owner is Accountable). */
    defaultRaci?: string;
    defaultRole: string;
    /** The member and the user's name (for the notice). */
    onSubmit: (input: TeamMemberInput, name: string) => Promise<unknown>;
    onClose: () => void;
};

const RACI_HELP: Record<string, string> = {
    Responsible: 'Does the work on the category.',
    Accountable: 'Answers for the category and signs off decisions.',
    Consulted: 'Gives input before decisions are made.',
    Informed: 'Is kept up to date on decisions.',
};

/**
 * The shared add-person modal: used for "Add new category owner" and "Add team member". Mount it to open it. People are
 * picked from the app's users who can open this dataset; the team stores no names or emails of its own.
 */
export const PersonDialog: React.FC<Props> = ({title, submitLabel, roles, raci, defaultRole, defaultRaci = 'Consulted', onSubmit, onClose}) => {
    const {id} = useParams<{id: string}>();
    const {data: users = [], isLoading} = useAppUsersQuery(Number(id));
    const [user, setUser] = useState<AppUser | null>(null);
    const [role, setRole] = useState(defaultRole);
    const [raciValue, setRaci] = useState(defaultRaci);
    const [busy, setBusy] = useState(false);

    const submit = () => {
        if (!user) return;
        setBusy(true);
        onSubmit({userId: user.id, role, raci: raciValue}, user.name)
            .then(onClose)
            .catch(() => setBusy(false));
    };

    return (
        <Dialog open onClose={busy ? undefined : onClose} fullWidth maxWidth="xs" aria-labelledby="cp-person-title">
            <DialogTitle id="cp-person-title">{title}</DialogTitle>
            <DialogContent>
                <Stack gap={2} sx={{pt: 1}}>
                    <Autocomplete
                        options={users} loading={isLoading} value={user} onChange={(_, v) => setUser(v)}
                        getOptionLabel={u => u.email ? `${u.name} (${u.email})` : u.name}
                        isOptionEqualToValue={(a, b) => a.id === b.id}
                        renderInput={params => (
                            <TextField {...params} label="User of the app" required autoFocus size="small"
                                       helperText="People on the team are users of the app who can open this dataset."/>
                        )}
                    />
                    <TextField select label="Role" value={role} onChange={e => setRole(e.target.value)} size="small">
                        {roles.map(r => <MenuItem key={r} value={r}>{r}</MenuItem>)}
                    </TextField>
                    <TextField select label="RACI" value={raciValue} onChange={e => setRaci(e.target.value)} size="small"
                               helperText={RACI_HELP[raciValue]}>
                        {raci.map(p => <MenuItem key={p} value={p}>{p}</MenuItem>)}
                    </TextField>
                </Stack>
            </DialogContent>
            <DialogActions sx={{px: 3, pb: 2}}>
                <Button onClick={onClose} disabled={busy}>Cancel</Button>
                <Button variant="contained" onClick={submit} disabled={busy || !user}>
                    {submitLabel}
                </Button>
            </DialogActions>
        </Dialog>
    );
};
